import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  public_service_provider_api_requests,
  service_provider_api_requests,
  SERVICE_PROVIDER_API,
} from "..";
import { BaseDto, FilterGroup, NormalFilter } from "../../util/baseDto";
import { useExport } from "../../util/useExport";
import { useNotification } from "../../../components/common/GlobalNotification";
import { useMemo } from "react";

const PATH = "/category";
const BASE_QUERY_KEY = SERVICE_PROVIDER_API + PATH;

export interface ServiceCategoryItem extends BaseDto {
  id: number;
  categoryName: string;
  searchKeywordIds: number[] | null;
  isPopularSearch?: boolean;
  imageS3Key?: string | null;
  imageUrl?: string | null;
  metaDescription?: string | null;
  categoryTypeId?: number | null;
  categoryTypeName?: string | null;
  /** EPIC-13 #73: "visit fee applies" (default true). */
  visitFeeApplicable?: boolean;
}

export interface ServiceCategoryApiResponse {
  metaData: {
    size: number;
    totalPages: number;
    page: number;
    totalElements: number;
  };
  data: ServiceCategoryItem[];
}

export interface CreateServiceCategoryRequest {
  categoryName: string;
  searchKeywordIds?: number[] | null;
  categoryTypeId?: number | null;
  visitFeeApplicable?: boolean;
}

export interface UpdateServiceCategoryRequest {
  categoryName: string;
  searchKeywordIds?: number[] | null;
  categoryTypeId?: number | null;
  visitFeeApplicable?: boolean;
}

export interface CreateServiceCategoryResponse {
  id: number;
  categoryName: string;
  searchKeywordIds?: number[] | null;
}

export function buildCategoryFormData(
  request: Record<string, any>,
  file?: File | null
): FormData {
  const formData = new FormData();

  formData.append(
    "data",
    new Blob([JSON.stringify(request)], { type: "application/json" })
  );

  if (file) {
    formData.append("file", file);
  }

  return formData;
}

const useCreateServiceCategory = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      request,
      file,
    }: {
      request: CreateServiceCategoryRequest;
      file?: File | null;
    }) => {
      const formData = buildCategoryFormData(request, file);
      return service_provider_api_requests.post(PATH, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service category created successfully");
    },
    onError: () => {
      setErrorNotification("Failed to create service category");
    },
  });
};

const useUpdateServiceCategory = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      id,
      request,
      file,
    }: {
      id: number;
      request: UpdateServiceCategoryRequest;
      file?: File | null;
    }) => {
      const formData = buildCategoryFormData(request, file);
      return service_provider_api_requests.put(`${PATH}/${id}`, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service category updated successfully");
    },
    onError: () => {
      setErrorNotification("Failed to update service category");
    },
  });
};

const useSearchServiceCategories = (
  size: number,
  page: number,
  categoryName?: string,
  city?: string
) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [
        ...(categoryName
          ? [NormalFilter("categoryName", "LIKE", categoryName)]
          : []),
      ],
    }),
    [categoryName]
  );

  return useInfiniteQuery<ServiceCategoryApiResponse, Error>({
    queryKey: [BASE_QUERY_KEY, "paged", categoryName, size, city],
    queryFn: async ({ pageParam = page }) =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=${pageParam}&size=${size}&sort=categoryName,asc${
          city?.trim() ? `&city=${encodeURIComponent(city)}` : ""
        }`,
        filterGroup
      ),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metaData.page ?? 0;
      const totalPages = lastPage.metaData.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useSearchPopularServiceCategories = (
  size: number,
  page: number,
  categoryName?: string,
  city?: string
) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [
        NormalFilter("isPopularSearch", "EQUALS", true),
        ...(categoryName
          ? [NormalFilter("categoryName", "LIKE", categoryName)]
          : []),
      ],
    }),
    [categoryName]
  );

  return useInfiniteQuery<ServiceCategoryApiResponse, Error>({
    queryKey: [BASE_QUERY_KEY, "popular-paged", categoryName, size, city],
    queryFn: async ({ pageParam = page }) =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=${pageParam}&size=${size}&sort=categoryName,asc${
          city?.trim() ? `&city=${encodeURIComponent(city)}` : ""
        }`,
        filterGroup
      ),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metaData.page ?? 0;
      const totalPages = lastPage.metaData.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetServiceCategories = (sort: string) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "categories", sort],
    queryFn: async (): Promise<ServiceCategoryApiResponse> =>
      public_service_provider_api_requests.get(
        `${PATH}/public/all?page=0&size=10&sort=categoryName,asc`
      ),
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const usePaginatedServiceCategories = (size = 10) => {
  const filterGroup: FilterGroup = { operator: "AND", criteria: [] };
  return useInfiniteQuery<ServiceCategoryApiResponse, Error>({
    queryKey: [BASE_QUERY_KEY, "infinite", size],
    queryFn: async ({ pageParam = 0 }) =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=${pageParam}&size=${size}&sort=categoryName,asc`,
        filterGroup
      ),
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.metaData;
      return page + 1 < totalPages ? page + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useGetServiceCategoryById = (id: number) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "service-category", id],
    queryFn: async (): Promise<ServiceCategoryItem> =>
      service_provider_api_requests.get(`${PATH}/${id}`),
    enabled: !!id,
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useDeleteServiceCategory = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (id: number): Promise<void> =>
      service_provider_api_requests.delete(`${PATH}/${id}`),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BASE_QUERY_KEY],
      });
      setSuccessNotification("Service category deleted successfully");
    },

    onError: () => {
      setErrorNotification("Failed to delete service category");
    },
  });
};

/**
 * Account Management pickers (PM, 2026-10-24): categories grouped under their category type, types A-Z,
 * categories A-Z (case-insensitive) inside each; categories without a type last under "Other".
 */
export const groupCategoriesByType = <T extends { categoryName: string; categoryTypeName?: string | null }>(
  categories: T[],
): { typeName: string; categories: T[] }[] => {
  const byName = (a: string, b: string) => a.localeCompare(b, "en", { sensitivity: "base" });
  const groups = new Map<string, T[]>();
  categories.forEach((c) => {
    const type = c.categoryTypeName?.trim() || "";
    groups.set(type, [...(groups.get(type) ?? []), c]);
  });
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : byName(a, b)))
    .map(([type, items]) => ({
      typeName: type || "Other",
      categories: [...items].sort((a, b) => byName(a.categoryName, b.categoryName)),
    }));
};

export const useExportServiceCategories = () =>
  useExport(
    (filterGroup) => public_service_provider_api_requests.export(`${PATH}/public/export`, filterGroup),
    "categories.xlsx",
    "Failed to export Service Categories",
  );

export {
  BASE_QUERY_KEY as SERVICE_CATEGORY_DETAILS_QUERY_KEY,
  useCreateServiceCategory,
  useUpdateServiceCategory,
  useSearchServiceCategories,
  useGetServiceCategories,
  useGetServiceCategoryById,
  usePaginatedServiceCategories,
  useSearchPopularServiceCategories,
  useDeleteServiceCategory,
};
